import React, { useEffect, useState, useRef } from 'react';
import { useTranslation } from 'react-i18next';

const isBrowser = typeof window !== 'undefined';
const SIDEBAR_BREAKPOINT = 960;

const ApiDocLanguageDropdown = ({ isShow }) => {
	const { t, i18n } = useTranslation();
	const [defaultLangauge, setDefaultLangauge] = useState({});
	const [isInitialized, setIsInitialized] = useState(false);
	const [isDesktop, setIsDesktop] = useState(
		isBrowser ? window.innerWidth >= SIDEBAR_BREAKPOINT : true
	);
	const [isOpen, setIsOpen] = useState(false);
	const dropdownRef = useRef(null);

	const languages = [
		{
			name: 'french',
			langCode: 'fr',
			img: 'https://d2lxkizvrhu4im.cloudfront.net/icons/flag-of-france-round.svg',
		},
		{
			name: 'english',
			langCode: 'en',
			img: 'https://d2lxkizvrhu4im.cloudfront.net/icons/flag-of-uk-round.svg',
		},
		{
			name: 'spanish',
			langCode: 'es',
			img: 'https://d2lxkizvrhu4im.cloudfront.net/icons/flag-of-spain-round.svg',
		},
		{
			name: 'italian',
			langCode: 'it',
			img: 'https://d2lxkizvrhu4im.cloudfront.net/icons/flag-of-italy-round.svg',
		},
		{
			name: 'portuguese_brazil',
			langCode: 'pt',
			img: 'https://d2lxkizvrhu4im.cloudfront.net/icons/flag-of-brazil-round.svg',
		},
		{
			name: 'german',
			langCode: 'de',
			img: 'https://d2lxkizvrhu4im.cloudfront.net/icons/flag-of-germany-round.svg',
		},
		{
			name: 'dutch',
			langCode: 'nl',
			img: 'https://d2lxkizvrhu4im.cloudfront.net/icons/flag-of-netherlands-round.svg',
		},
		{
			name: 'welsh',
			langCode: 'cy',
			img: 'https://mereos-corder.s3.eu-west-3.amazonaws.com/library_icons/flag-of-whales-round.svg',
		},
	];

	useEffect(() => {
		if (!isBrowser) return;
		const handleResize = () => {
			setIsDesktop(window.innerWidth >= SIDEBAR_BREAKPOINT);
		};
		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, []);

	useEffect(() => {
		if (!isBrowser) return;
		const handleClickOutside = (e) => {
			if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
				setIsOpen(false);
			}
		};
		document.addEventListener('mousedown', handleClickOutside);
		return () => document.removeEventListener('mousedown', handleClickOutside);
	}, []);

	const setLanguageFromI18n = () => {
		const currentLang = i18n.language;
		const baseLang = currentLang.split('-')[0];
		const selectedLanguage = languages.find((language) => language.langCode === baseLang);

		if (selectedLanguage) {
			setDefaultLangauge(selectedLanguage);
		} else {
			const englishLang = languages.find((lang) => lang.langCode === 'en') || languages[0];
			setDefaultLangauge(englishLang);
		}
	};

	useEffect(() => {
		if (!isBrowser) return;

		const initializeLanguage = () => {
			const savedLanguage = localStorage.getItem('selectedLanguage');

			if (savedLanguage) {
				try {
					const parsedLanguage = JSON.parse(savedLanguage);
					const languageExists = languages.some(
						(lang) => lang.langCode === parsedLanguage.langCode
					);

					if (languageExists) {
						setDefaultLangauge(parsedLanguage);
						if (parsedLanguage.langCode !== i18n.language) {
							i18n.changeLanguage(parsedLanguage.langCode);
						}
					} else {
						setLanguageFromI18n();
					}
				} catch {
					setLanguageFromI18n();
				}
			} else {
				setLanguageFromI18n();
			}

			setIsInitialized(true);
		};

		initializeLanguage();
	}, []);

	useEffect(() => {
		if (!isBrowser || !isInitialized) return;

		const currentLang = i18n.language;
		const baseLang = currentLang.split('-')[0];
		const selectedLanguage = languages.find((language) => language.langCode === baseLang);

		if (selectedLanguage && selectedLanguage.langCode !== defaultLangauge.langCode) {
			setDefaultLangauge(selectedLanguage);
		}
	}, [i18n.language, isInitialized]);

	const handleLanguageChange = (e, language) => {
		if (!isBrowser) return;

		e.stopPropagation();
		e.preventDefault();

		i18n.changeLanguage(language.langCode);
		setDefaultLangauge(language);
		setIsOpen(false);

		try {
			localStorage.setItem('selectedLanguage', JSON.stringify(language));
		} catch (error) {
			console.error('Failed to save language preference:', error);
		}
	};

	if (!isInitialized || !isBrowser) return null;

	const shouldRender = isShow || isDesktop;
	if (!shouldRender) return null;

	return (
		<div
			ref={dropdownRef}
			className='flex flex-row items-center justify-center leading-5 font-semibold'
		>
			<div className='relative focus:outline-none cursor-pointer'>
				<div className='flex flex-row items-center mr-6' onClick={() => setIsOpen((prev) => !prev)}>
					{defaultLangauge?.img && (
						<img
							alt={defaultLangauge.name}
							src={defaultLangauge.img}
							className='my-0 w-6'
							title={t(defaultLangauge.name)}
						/>
					)}
					<img
						alt='toggle'
						src='https://d2lxkizvrhu4im.cloudfront.net/icons/arrow-down_yellow_light_icon.svg.svg'
						className='my-0 ml-2'
						style={{
							transition: 'transform 0.2s ease',
							transform: isOpen ? 'rotate(180deg)' : 'rotate(0deg)',
						}}
					/>
				</div>

				{isOpen && (
					<div className='absolute z-10' style={{ right: 0, left: 'auto' }}>
						<div className='relative flex flex-col overflow-visible bg-white rounded-lg p-5 space-y-2 font-normal shadow-xl'>
							{languages.map((lang, index) =>
								lang.langCode !== defaultLangauge?.langCode ? (
									<img
										key={index}
										alt={lang.name}
										src={lang.img}
										className='my-0 ml-2 w-6 cursor-pointer hover:opacity-80 transition-opacity'
										title={t(lang.name)}
										onClick={(e) => handleLanguageChange(e, lang)}
									/>
								) : null
							)}
						</div>
					</div>
				)}
			</div>
		</div>
	);
};

export default ApiDocLanguageDropdown;
